import React, { createContext, useCallback, useContext, useState } from 'react';

// Lets each admin page contribute its own contextual buttons to the right-side
// quick-actions panel instead of rendering yet-another-page-header full of
// action buttons.
//
// Usage from a page:
//   const { setActions } = useSidebarActions();
//   useEffect(() => {
//     setActions([{ label: 'Add Vendor', icon: <AddIcon />, onClick: () => ... }]);
//     return () => setActions([]);
//   }, [...]);
//
// Also exposes global admin-wide actions (e.g. "Register New Vendor") and a
// `vendorChangeNonce` counter that pages can include in their effect deps to
// re-fetch when a vendor is created or modified from anywhere in the shell.
const SidebarActionsContext = createContext({
  actions: [],
  setActions: () => {},
  rightPanel: null,
  setRightPanel: () => {},
  openRegisterVendor: () => {},
  openImportReturning: () => {},
  notifyVendorChanged: () => {},
  vendorChangeNonce: 0,
});

export const SidebarActionsProvider = ({
  children,
  openRegisterVendor,
  openImportReturning,
  notifyVendorChanged,
  vendorChangeNonce,
}) => {
  const [actions, setActionsState] = useState([]);
  // When a page sets rightPanel to a React node, AdminLayout swaps the entire
  // right sidebar for that node instead of rendering the default Stats + Quick
  // Actions stack. Used by pages like the Map Editor that want full control of
  // their tools panel.
  const [rightPanel, setRightPanelState] = useState(null);

  const setActions = useCallback((next) => {
    setActionsState(Array.isArray(next) ? next : []);
  }, []);

  const setRightPanel = useCallback((node) => {
    setRightPanelState(node ?? null);
  }, []);

  return (
    <SidebarActionsContext.Provider
      value={{
        actions,
        setActions,
        rightPanel,
        setRightPanel,
        openRegisterVendor: openRegisterVendor || (() => {}),
        openImportReturning: openImportReturning || (() => {}),
        notifyVendorChanged: notifyVendorChanged || (() => {}),
        vendorChangeNonce: vendorChangeNonce || 0,
      }}
    >
      {children}
    </SidebarActionsContext.Provider>
  );
};

export const useSidebarActions = () => useContext(SidebarActionsContext);
